import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  passPublic,
  passReservations,
  passPayments,
  rememberAffiliate,
  getRememberedAffiliate,
} from '../../services/passApi';
import { Gift, AlertCircle, Loader2, CreditCard, Calendar, Flame, TrendingUp, Plus, Minus, Package } from 'lucide-react';

const PAID_MODES = new Set(['deposit', 'full_payment', 'cover_payment']);
const DATE_REQUIRED_MODES = new Set(['deposit', 'full_payment', 'cover_payment', 'reservation_only']);

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

const MODE_LABEL = {
  deposit: 'Apartar (pagar depósito)',
  full_payment: 'Pagar ahora',
  cover_payment: 'Pagar cover',
  reservation_only: 'Reservar',
  manual_lead: 'Quiero esta promoción',
};

export default function OfferDetail() {
  const { offerSlug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [offer, setOffer] = useState(null);
  const [error, setError] = useState(null);
  const [claiming, setClaiming] = useState(false);

  const [form, setForm] = useState({ name: '', phone: '', email: '', people: 1, date: todayISO() });
  const [availability, setAvailability] = useState(null);
  const [surge, setSurge] = useState(null);
  const [addonQty, setAddonQty] = useState({}); // { addon_id: qty }

  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      rememberAffiliate(null, ref);
    }
  }, [searchParams]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    passPublic
      .offer(offerSlug)
      .then((res) => alive && setOffer(res.data.offer))
      .catch((err) => alive && setError(err.response?.data?.detail || 'No encontrada'))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [offerSlug]);

  // Re-query availability when offer loads OR date changes
  useEffect(() => {
    if (!offer) return;
    let alive = true;
    const date = DATE_REQUIRED_MODES.has(offer.mode) ? form.date : null;
    passPublic
      .availability(offer.slug, date)
      .then((res) => {
        if (!alive) return;
        setAvailability(res.data.availability);
        setSurge(res.data.surge);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [offer, form.date]);

  async function handleClaim(e) {
    e.preventDefault();
    if (!offer) return;
    setClaiming(true);
    try {
      const attribution = getRememberedAffiliate();
      const affiliateCode = attribution?.code || searchParams.get('ref') || null;
      const isPaid = PAID_MODES.has(offer.mode);
      const needsDate = DATE_REQUIRED_MODES.has(offer.mode);

      const addonsSelected = Object.entries(addonQty)
        .filter(([, q]) => Number(q) > 0)
        .map(([id, q]) => ({ addon_id: id, quantity: Number(q) }));

      const basePayload = {
        offer_id: offer.id,
        affiliate_code: affiliateCode,
        people_count: Number(form.people) || 1,
        customer: {
          name: form.name || null,
          phone: form.phone || null,
          email: form.email || null,
        },
        reservation_date: needsDate ? form.date : null,
        addons: addonsSelected,
      };

      // For free_claim, if any selected addon is pay_in_app → push the whole thing to checkout flow
      const hasOnlineAddon = addonsSelected.some((sel) => {
        const def = (offer.addons || []).find((a) => a.id === sel.addon_id);
        return def && def.pay_mode === 'pay_in_app' && Number(def.price) > 0 && Number(sel.quantity) > 0;
      });

      if (isPaid || hasOnlineAddon) {
        if (!form.phone) {
          toast.error('WhatsApp es obligatorio para reservas con pago');
          setClaiming(false);
          return;
        }
        const res = await passPayments.createCheckoutSession({
          ...basePayload,
          return_origin: window.location.origin,
        });
        // Redirect to Stripe Checkout
        window.location.href = res.data.checkout_url;
        return;
      }

      const res = await passReservations.claimFree(basePayload);
      toast.success('Promo reclamada — guarda tu QR');
      navigate(`/pass/ticket/${res.data.qr_token}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo completar');
    } finally {
      setClaiming(false);
    }
  }

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Cargando...</div>;
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="text-center">
          <AlertCircle className="w-10 h-10 mx-auto text-orange-500 mb-3" />
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  const isFreeClaim = offer.mode === 'free_claim';
  const isPaid = PAID_MODES.has(offer.mode);
  const needsDate = DATE_REQUIRED_MODES.has(offer.mode);
  const benefit = offer.customer_benefits?.[0]?.name;
  const includedItems = offer.included_items || [];
  const offerAddons = offer.addons || [];
  const pricing = offer.pricing || {};

  // If surge applies, take its prices instead
  const effectiveBase = surge?.applied ? surge.base_price : Number(pricing.base_price || 0);
  const effectivePayInApp = surge?.applied ? surge.pay_in_app : Number(pricing.pay_in_app || 0);
  const basePayNow = isPaid
    ? offer.mode === 'full_payment'
      ? effectiveBase
      : effectivePayInApp
    : 0;

  // Addon totals
  const addonOnlineTotal = offerAddons.reduce((sum, a) => {
    const q = Number(addonQty[a.id] || 0);
    return a.pay_mode === 'pay_in_app' ? sum + q * Number(a.price || 0) : sum;
  }, 0);
  const addonAtBizTotal = offerAddons.reduce((sum, a) => {
    const q = Number(addonQty[a.id] || 0);
    return a.pay_mode === 'pay_at_business' ? sum + q * Number(a.price || 0) : sum;
  }, 0);

  const payNow = basePayNow + addonOnlineTotal;
  const pendingAtBiz = isPaid
    ? Math.max(0, effectiveBase - basePayNow) + addonAtBizTotal
    : addonAtBizTotal;
  const totalExperience = (isPaid ? effectiveBase : 0) + addonOnlineTotal + addonAtBizTotal;
  const actionLabel = MODE_LABEL[offer.mode] || 'Continuar';

  const capLow = availability?.enabled && availability.remaining !== null && availability.remaining <= 5;
  const capSoldOut = availability?.enabled && availability.remaining === 0;

  function adjustAddon(addonId, delta, maxQty) {
    setAddonQty((q) => {
      const current = Number(q[addonId] || 0);
      const next = Math.max(0, Math.min(maxQty, current + delta));
      return { ...q, [addonId]: next };
    });
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-900 text-white py-10 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-xs uppercase tracking-wide text-teal-300 font-semibold mb-2">{offer.category}</div>
          <h1 className="text-3xl md:text-4xl font-bold">{offer.title}</h1>
          {benefit && (
            <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 bg-orange-500/20 text-orange-300 rounded-full">
              <Gift className="w-4 h-4" /> <span className="font-semibold">{benefit}</span>
            </div>
          )}
        </div>
      </header>

      <OfferGallery images={offer.images || []} title={offer.title} />

      <main className="max-w-3xl mx-auto px-6 py-8">
        {offer.description && <p className="text-slate-700 mb-4">{offer.description}</p>}

        {/* Included items */}
        {includedItems.length > 0 && (
          <div className="mb-4 bg-teal-50 border border-teal-200 rounded-xl p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-teal-700 mb-2 inline-flex items-center gap-1">
              <Package className="w-3.5 h-3.5" /> Incluido en el precio
            </p>
            <ul className="space-y-1">
              {includedItems.map((it, idx) => (
                <li key={idx} className="text-sm text-slate-700">
                  <span className="font-semibold">{it.name}</span>
                  {it.description && <span className="text-slate-500"> — {it.description}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Status badges row */}
        <div className="flex flex-wrap gap-2 mb-4">
          {availability?.enabled && !capSoldOut && (
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${capLow ? 'bg-red-50 text-red-700' : 'bg-slate-100 text-slate-700'}`}>
              {capLow ? '🔥 ' : ''}Quedan {availability.remaining} cupos{availability.scope === 'daily' ? ' para esta fecha' : ''}
            </span>
          )}
          {capSoldOut && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-100 text-red-700 text-xs font-semibold">
              Sin cupo disponible
            </span>
          )}
          {surge?.applied && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-orange-100 text-orange-700 text-xs font-semibold">
              <Flame className="w-3 h-3" /> Demanda alta — precio actualizado ({surge.summary})
            </span>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          {(isFreeClaim || isPaid) ? (
            <form onSubmit={handleClaim} className="space-y-4">
              <h2 className="text-lg font-semibold text-slate-900">
                {isPaid ? 'Completar reserva' : 'Reclamar promoción'}
              </h2>
              <p className="text-sm text-slate-500">
                {isPaid
                  ? 'Captura tus datos. Continuarás a un pago seguro con Stripe.'
                  : 'Captura tus datos. Te entregaremos un QR para presentar al llegar.'}
              </p>
              <input
                className="w-full border border-slate-300 rounded-lg px-3 py-2"
                placeholder={isPaid ? 'Tu nombre completo *' : 'Tu nombre (opcional)'}
                required={isPaid}
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
              <input
                className="w-full border border-slate-300 rounded-lg px-3 py-2"
                placeholder={isPaid ? 'WhatsApp (10 dígitos) *' : 'WhatsApp (opcional)'}
                required={isPaid}
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              />
              {isPaid && (
                <input
                  type="email"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2"
                  placeholder="Email para recibo (opcional)"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                />
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-500">Personas</label>
                  <input
                    type="number"
                    min={1}
                    max={20}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2"
                    value={form.people}
                    onChange={(e) => setForm((f) => ({ ...f, people: e.target.value }))}
                  />
                </div>
                {needsDate && (
                  <div>
                    <label className="text-xs text-slate-500 inline-flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" /> Fecha
                    </label>
                    <input
                      type="date"
                      min={todayISO()}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2"
                      value={form.date}
                      onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                    />
                  </div>
                )}
              </div>

              {/* Addons selector */}
              {offerAddons.length > 0 && (
                <div className="border border-slate-200 rounded-lg p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 inline-flex items-center gap-1">
                    <Package className="w-3.5 h-3.5" /> Adicionales (opcional)
                  </p>
                  <div className="space-y-2">
                    {offerAddons.map((a) => {
                      const qty = Number(addonQty[a.id] || 0);
                      return (
                        <div key={a.id} className="flex items-center justify-between gap-2 text-sm">
                          <div className="flex-1">
                            <p className="font-semibold text-slate-900">{a.name}</p>
                            <p className="text-xs text-slate-500">
                              ${Number(a.price || 0).toFixed(2)} {pricing.currency || 'MXN'} · {a.pay_mode === 'pay_in_app' ? 'online' : 'en negocio'}
                            </p>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => adjustAddon(a.id, -1, a.max_quantity || 1)}
                              disabled={qty === 0}
                              className="w-7 h-7 rounded-full border border-slate-300 inline-flex items-center justify-center hover:bg-slate-50 disabled:opacity-30"
                            >
                              <Minus className="w-3 h-3" />
                            </button>
                            <span className="w-6 text-center font-semibold text-slate-900">{qty}</span>
                            <button
                              type="button"
                              onClick={() => adjustAddon(a.id, 1, a.max_quantity || 1)}
                              disabled={qty >= (a.max_quantity || 1)}
                              className="w-7 h-7 rounded-full border border-slate-300 inline-flex items-center justify-center hover:bg-slate-50 disabled:opacity-30"
                            >
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {(isPaid || addonOnlineTotal > 0 || addonAtBizTotal > 0) && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm space-y-1">
                  {isPaid && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Base de la oferta</span>
                      <span className="font-semibold text-slate-900">
                        ${effectiveBase.toFixed(2)}
                        {surge?.applied && (
                          <span className="ml-1 text-xs text-orange-600 inline-flex items-center gap-0.5">
                            <TrendingUp className="w-3 h-3" /> surge
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                  {(addonOnlineTotal + addonAtBizTotal) > 0 && (
                    <div className="flex justify-between text-slate-600">
                      <span>Adicionales</span>
                      <span>+${(addonOnlineTotal + addonAtBizTotal).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t border-slate-200 pt-1">
                    <span className="text-slate-500">Total experiencia</span>
                    <span className="font-semibold text-slate-900">${totalExperience.toFixed(2)} {pricing.currency || 'MXN'}</span>
                  </div>
                  <div className="flex justify-between text-orange-600 font-semibold">
                    <span>Pagas ahora</span>
                    <span>${payNow.toFixed(2)}</span>
                  </div>
                  {pendingAtBiz > 0 && (
                    <div className="flex justify-between text-slate-600">
                      <span>Pendiente en el negocio</span>
                      <span>${pendingAtBiz.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              )}

              <button
                type="submit"
                disabled={claiming || capSoldOut}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-semibold disabled:opacity-60"
              >
                {claiming && <Loader2 className="w-4 h-4 animate-spin" />}
                {(isPaid || payNow > 0) ? <CreditCard className="w-4 h-4" /> : null}
                {capSoldOut
                  ? 'Sin cupo disponible'
                  : payNow > 0 ? `${actionLabel} — $${payNow.toFixed(2)}` : actionLabel}
              </button>

              {isPaid && (
                <p className="text-[11px] text-slate-400 text-center">
                  Pago procesado por Stripe. No guardamos datos de tu tarjeta.
                </p>
              )}
            </form>
          ) : (
            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-2">Esta promo es por reserva manual</h2>
              <p className="text-sm text-slate-500">El equipo se pondrá en contacto contigo después de la reserva.</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function OfferGallery({ images, title }) {
  const [activeIdx, setActiveIdx] = useState(0);

  if (!images || images.length === 0) return null;

  const active = images[activeIdx] || images[0];

  return (
    <section className="bg-slate-900 px-6 pb-6 -mt-4">
      <div className="max-w-3xl mx-auto">
        <div className="aspect-[16/9] sm:aspect-[21/9] bg-slate-800 rounded-2xl overflow-hidden shadow-xl">
          <img
            key={active}
            src={active}
            alt={title}
            className="w-full h-full object-cover transition-opacity"
          />
        </div>
        {images.length > 1 && (
          <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
            {images.map((url, idx) => (
              <button
                key={`${url}-${idx}`}
                type="button"
                onClick={() => setActiveIdx(idx)}
                className={`flex-shrink-0 w-20 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                  idx === activeIdx
                    ? 'border-orange-500 ring-2 ring-orange-500/40'
                    : 'border-transparent opacity-60 hover:opacity-100'
                }`}
              >
                <img src={url} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
